import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { X, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { API } from '../config/constants';

// Helper function to display cigar name without duplicating brand
const formatCigarName = (brand, name) => {
  if (!name || name.toLowerCase() === brand?.toLowerCase()) {
    return brand || 'Unknown';
  }
  return `${brand} ${name}`;
};

const NotesPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [loading, setLoading] = useState(true);
  const [cigar, setCigar] = useState(null);

  useEffect(() => {
    if (token) {
      fetchNotes();
      fetchCigar();
    }
  }, [id, token]);

  const fetchCigar = async () => {
    try {
      const res = await axios.get(`${API}/cigars/${id}`);
      setCigar(res.data);
    } catch (error) {
      console.error('Failed to fetch cigar');
    }
  };

  const fetchNotes = async () => {
    try {
      const res = await axios.get(`${API}/notes/cigar/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotes(res.data);
    } catch (error) {
      console.error('Failed to fetch notes');
    } finally {
      setLoading(false);
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) return;

    try {
      await axios.post(
        `${API}/notes`,
        { cigar_id: id, text: newNote },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNewNote('');
      fetchNotes();
      toast.success('Note added!');
    } catch (error) {
      toast.error('Failed to add note');
    }
  };

  const handleDeleteNote = async (noteId) => {
    try {
      await axios.delete(`${API}/notes/${noteId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchNotes();
      toast.success('Note deleted!');
    } catch (error) {
      toast.error('Failed to delete note');
    }
  };

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    const dateStr = date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
    const timeStr = date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
    return `${dateStr} ${timeStr}`;
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-[#0B0B0C] flex items-center justify-center">
        <div className="text-center">
          <p className="text-white/70 mb-4">Please sign in to view your notes</p>
          <Button onClick={() => navigate('/auth')} className="bg-amber-600">Sign In</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0B0B0C] flex flex-col">
      {/* Header */}
      <div className="flex items-center p-4 border-b border-white/10">
        <button onClick={() => navigate(-1)} className="text-white mr-4">
          <X size={24} />
        </button>
        <h1 className="playfair text-xl text-white flex-1 text-center pr-8">My Notes</h1>
      </div>

      {/* Cigar Info */}
      {cigar && (
        <div className="p-4 border-b border-white/10 bg-white/5">
          <p className="text-xs text-white/50">{cigar.brand}</p>
          <p className="text-white font-semibold">{formatCigarName(cigar.brand, cigar.name)}</p>
        </div>
      )}

      {/* Notes List */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {loading ? (
          <div className="text-center text-white/50 py-8">Loading...</div>
        ) : notes.length === 0 ? (
          <div className="text-center text-white/50 py-8">No notes yet. Add your first note below!</div>
        ) : (
          notes.map((note) => (
            <div key={note.id} className="bg-white/5 rounded-xl p-4">
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1">
                  <p className="text-white/90 whitespace-pre-wrap">{note.text}</p>
                  <p className="text-white/40 text-sm mt-2">{formatDateTime(note.created_at)}</p>
                </div>
                <button 
                  onClick={() => handleDeleteNote(note.id)}
                  className="text-white/40 hover:text-red-500 transition-colors p-1"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Add Note Input */}
      <div className="border-t border-white/10 p-4 bg-[#0B0B0C]">
        <div className="flex gap-2">
          <textarea
            value={newNote}
            onChange={(e) => setNewNote(e.target.value)}
            placeholder="Add a personal note..."
            className="flex-1 bg-white/5 border border-white/10 rounded-lg p-3 text-white placeholder:text-white/40 resize-none h-20"
          />
        </div>
        <Button 
          onClick={handleAddNote}
          disabled={!newNote.trim()}
          className="w-full mt-3 bg-amber-600 hover:bg-amber-700 disabled:opacity-50"
        >
          Add Note
        </Button>
      </div>
    </div>
  );
};

export default NotesPage;
